import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppStateStore } from '../../context/AppStateStore';

import { useModuleData } from './useModuleData';
import { useAttachmentMutations } from './task/useAttachmentMutations';

import { useUpdateTaskResourceProgress } from '../../api/endpoints/task-resources/task-resources';
import { useAuthStore } from '../../auth/store';

/**
 * Hook to manage the selected task state and its resources
 * Uses the new approach where resources come with tasks and are enhanced with thumbnails
 */
export const useSelectedTask = () => {
  const queryClient = useQueryClient();

  // selected module and task
  const moduleId = useAppStateStore((state) => state.selectedModule?.moduleId);
  const courseId = useAppStateStore((state) => state.selectedModule?.courseId);
  const selectedTaskIds = useAppStateStore((state) => state.selectedTaskIds);
  const taskId = moduleId ? selectedTaskIds[moduleId] : null;

  const {
    tasks,
    isLoading: isTasksLoading,
    isError: isTasksError,
    error: tasksError,
    queryKey: moduleDataQueryKey,
  } = useModuleData({
    moduleId,
    courseId,
    includes: ['submissions', 'resources'],
  });

  const selectedTaskData = Array.isArray(tasks) && tasks.find((t) => t.id === taskId);

  // Calculate module state based on tasks
  const allTasksCompleted = useMemo(
    () => tasks?.every((task) => task.submission?.state === 'marked_as_done') || false,
    [tasks],
  );

  const thumbnails = useMemo(() => {
    // Check if selectedTaskData and resources exist and resources is an array
    if (!selectedTaskData || !selectedTaskData.resources || !Array.isArray(selectedTaskData.resources)) {
      return {};
    }

    return selectedTaskData.resources.reduce((acc, resource) => {
      acc[resource.id] = resource.thumbnailUrls?.small;
      return acc;
    }, {});
  }, [selectedTaskData]);

  const isThumbnailsLoading = false;

  const { uploadAttachmentMutation, deleteAttachmentMutation } = useAttachmentMutations();

  const impersonatedBy = useAuthStore((state) => state.impersonatedBy);

  // Use task-specific progress update to avoid wholesale invalidation
  const updateProgress = useUpdateTaskResourceProgress({
    mutation: {
      retry: false,
      // Use optimistic updates for faster UI feedback
      onMutate: async (variables) => {
        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries(moduleDataQueryKey);

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(moduleDataQueryKey);

        // Optimistically update the cache with new progress
        if (previousData) {
          queryClient.setQueryData(moduleDataQueryKey, (oldData) => {
            if (!oldData || !oldData.data || !oldData.data.data) {
              return oldData;
            }

            // Deep clone the data to avoid reference issues
            const newData = JSON.parse(JSON.stringify(oldData));
            const taskIndex = newData.data.data.findIndex((task) => task.id === variables.taskId);

            if (taskIndex !== -1) {
              const resourceIndex = newData.data.data[taskIndex].resources.findIndex(
                (resource) => resource.id === variables.resourceId,
              );

              if (resourceIndex !== -1) {
                // Update with the new progress data
                newData.data.data[taskIndex].resources[resourceIndex].progress = {
                  ...newData.data.data[taskIndex].resources[resourceIndex].progress,
                  ...variables.data,
                  taskId: variables.taskId,
                  resourceId: variables.resourceId,
                };
              }
            }

            return newData;
          });
        }

        // Return the previous value in case we need to roll back
        return { previousData };
      },

      onError: (error, variables, context) => {
        // If the mutation fails, roll back to the previous value
        if (context?.previousData) {
          queryClient.setQueryData(moduleDataQueryKey, context.previousData);
        }
      },
    },
  });

  // Wrap updateProgress to be a no-op on the server when impersonating,
  // while still allowing optimistic updates to function in the UI.
  const silencedUpdateProgress = useMemo(() => {
    if (!impersonatedBy) return updateProgress;
    return {
      ...updateProgress,
      mutate: () => Promise.resolve(),
      mutateAsync: () => Promise.resolve(),
    };
  }, [updateProgress, impersonatedBy]);

  return {
    // data
    task: selectedTaskData,
    resources: selectedTaskData?.resources || [],
    thumbnails,
    allTasksCompleted,
    // states
    isTasksLoading,
    isThumbnailsLoading,
    isTasksError,
    tasksError,
    // mutations
    updateProgress: silencedUpdateProgress,
    uploadAttachmentMutation,
    deleteAttachmentMutation,
  };
};

export default useSelectedTask;
